import { useState, useEffect } from "react";
import { supabase, CompanyRow } from "../lib/supabase";
import { companies as mockCompanies, getCompanyById as getMockCompanyById, Company as MockCompany } from "../mocks/companies";

export interface CompanyBasic {
  id: string;
  name: string;
  nameKr: string;
  taglineVi: string;
  taglineKr: string;
  descriptionVi: string;
  descriptionKr: string;
  logoInitials: string;
  logoIcon: string;
  logoFrom: string;
  logoTo: string;
  logoUrl: string;
  founded: string;
  location: string;
  employees: string;
  mainCategoryId: string;
  mainCategoryName: string;
  mainCategoryNameKr: string;
  website: string;
  images: string[];
  extraLinks: { url: string; label: string }[];
  patentsCertifications: { title?: string; image?: string; description?: string; image_url?: string }[];
  products: { id: string; name: string; nameKr: string; descriptionVi: string; descriptionKr: string; icon: string; tags: string[] }[];
}

function mapRowToBasic(row: CompanyRow): CompanyBasic {
  const rawRow = row as unknown as Record<string, unknown>;
  return {
    id: row.id,
    name: row.name,
    nameKr: row.name_kr ?? "",
    taglineVi: row.tagline_vi ?? "",
    taglineKr: row.tagline_kr ?? "",
    descriptionVi: row.description_vi ?? "",
    descriptionKr: row.description_kr ?? "",
    logoInitials: row.logo_initials ?? row.name.slice(0, 2).toUpperCase(),
    logoIcon: row.logo_icon ?? "ri-building-line",
    logoFrom: row.logo_from ?? "#1C4E6B",
    logoTo: row.logo_to ?? "#2B7A9C",
    logoUrl: row.logo_url ?? "",
    founded: row.founded ?? "",
    location: row.location ?? "",
    employees: row.employees ?? "",
    mainCategoryId: (rawRow.category_id as string) ?? "",
    mainCategoryName: row.main_category_name ?? "",
    mainCategoryNameKr: row.main_category_name_kr ?? "",
    website: row.website ?? "",
    images: Array.isArray(rawRow.images) ? (rawRow.images as string[]) : [],
    extraLinks: Array.isArray(rawRow.extra_links) ? (rawRow.extra_links as { url: string; label: string }[]) : [],
    patentsCertifications: Array.isArray(rawRow.patents_certifications) ? (rawRow.patents_certifications as { title?: string; image?: string; description?: string; image_url?: string }[]) : [],
    products: [],
  };
}

function mapMockToBasic(c: MockCompany): CompanyBasic {
  return {
    id: c.id,
    name: c.name,
    nameKr: c.nameKr,
    taglineVi: c.taglineVi,
    taglineKr: c.taglineKr,
    descriptionVi: c.descriptionVi,
    descriptionKr: c.descriptionKr,
    logoInitials: c.logoInitials,
    logoIcon: c.logoIcon,
    logoFrom: c.logoFrom,
    logoTo: c.logoTo,
    logoUrl: "",
    founded: c.founded,
    location: c.location,
    employees: c.employees,
    mainCategoryId: "",
    mainCategoryName: c.mainCategoryName,
    mainCategoryNameKr: c.mainCategoryNameKr,
    website: "",
    images: [],
    extraLinks: [],
    patentsCertifications: [],
    products: c.products.map((p) => ({
      id: p.id,
      name: p.name,
      nameKr: p.nameKr,
      descriptionVi: p.descriptionVi,
      descriptionKr: p.descriptionKr,
      icon: p.icon,
      tags: p.tags,
    })),
  };
}

/** Fetch all companies (Supabase first, mock fallback) */
export const useCompanies = () => {
  const [companies, setCompanies] = useState<CompanyBasic[]>([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"supabase" | "mock">("mock");

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("companies")
          .select("*")
          .order("created_at", { ascending: false });
        if (!cancelled && !error && data && data.length > 0) {
          setCompanies((data as CompanyRow[]).map(mapRowToBasic));
          setSource("supabase");
          setLoading(false);
          return;
        }
      } catch {/* fallthrough */}
      if (!cancelled) {
        setCompanies(mockCompanies.map(mapMockToBasic));
        setSource("mock");
        setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  return { companies, loading, source };
};

/** Fetch single company by ID */
export const useCompany = (id: string | undefined) => {
  const [company, setCompany] = useState<CompanyBasic | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"supabase" | "mock" | null>(null);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let cancelled = false;
    const fetchOne = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("companies")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (!cancelled && !error && data) {
          setCompany(mapRowToBasic(data as CompanyRow));
          setSource("supabase");
          setLoading(false);
          return;
        }
      } catch {/* fallthrough */}
      if (!cancelled) {
        const mock = getMockCompanyById(id);
        if (mock) {
          setCompany(mapMockToBasic(mock));
          setSource("mock");
        } else {
          setCompany(null);
          setSource(null);
        }
        setLoading(false);
      }
    };
    fetchOne();
    return () => { cancelled = true; };
  }, [id]);

  return { company, loading, source };
};
